import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";
import { navigate, OVERLAY_ONLY } from "../App";

const BRAND = "#FF3C00";

const VALID = new Set(["ai-overlay", "website-rework", "langzeit-betreuung"]);

export function OfferingPage({ slug }: { slug: string }) {
  const { t } = useTranslation("offering");
  if (!VALID.has(slug)) return null;
  const o = {
    title: t(`${slug}.title`) as string,
    subtitle: t(`${slug}.subtitle`) as string,
    intro: t(`${slug}.intro`) as string,
    forWhom: t(`${slug}.forWhom`, { returnObjects: true }) as string[],
    notForWhom: t(`${slug}.notForWhom`, { returnObjects: true }) as string[],
    deliverables: t(`${slug}.deliverables`, { returnObjects: true }) as { title: string; body: string }[],
    process: t(`${slug}.process`, { returnObjects: true }) as { label: string; title: string; body: string }[],
    faq: t(`${slug}.faq`, { returnObjects: true }) as { q: string; a: string }[],
    pricing: t(`${slug}.pricing`, { returnObjects: true }) as { headline: string; lines: string[]; note?: string },
    related: t(`${slug}.related`, { returnObjects: true }) as { label: string; href: string }[],
  };

  // Website rework isn't offered when the AI_OVERLAY_ONLY gate is on, so drop
  // any cross-links to it from the related section.
  if (OVERLAY_ONLY) {
    o.related = o.related.filter((r) => r.href !== "/services/website-rework");
  }

  return (
    <SubPage title={o.title} subtitle={o.subtitle}>
      <section className="px-5 sm:px-8 md:px-[66px] py-16 md:py-20">
        <p className="text-grey-300 text-[17px] md:text-lg leading-relaxed max-w-2xl">{o.intro}</p>
      </section>
      <FullDivider />

      <section className="grid grid-cols-1 md:grid-cols-2">
        <div className="px-5 sm:px-8 md:px-[66px] py-14 md:py-16 border-b md:border-b-0 md:border-r border-grey-800">
          <h3 className="text-sm font-mono uppercase tracking-wider text-grey-500 mb-5">{t("forWhomLabel")}</h3>
          <ul className="flex flex-col gap-3">
            {o.forWhom.map((line) => (
              <li key={line} className="flex items-start gap-3 text-[14px] text-grey-300 leading-relaxed">
                <span className="font-bold mt-0.5 shrink-0 text-sm" style={{ color: BRAND }}>✓</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="px-5 sm:px-8 md:px-[66px] py-14 md:py-16">
          <h3 className="text-sm font-mono uppercase tracking-wider text-grey-500 mb-5">{t("notForLabel")}</h3>
          <ul className="flex flex-col gap-3">
            {o.notForWhom.map((line) => (
              <li key={line} className="flex items-start gap-3 text-[14px] text-grey-400 leading-relaxed">
                <span className="font-bold mt-0.5 shrink-0 text-sm text-grey-600">·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <FullDivider />

      <section className="px-5 sm:px-8 md:px-[66px] py-16 md:py-20">
        <h2 className="text-[28px] md:text-[36px] font-bold text-white tracking-tight mb-3 leading-tight">{t("deliverablesTitle")}</h2>
        <p className="text-grey-400 text-[15px] leading-relaxed max-w-2xl mb-12">{t("deliverablesLead")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800 border-y border-grey-800">
          {o.deliverables.map((d) => (
            <div key={d.title} className="bg-grey-975 p-6 md:p-8">
              <h3 className="text-base font-semibold text-white mb-2">{d.title}</h3>
              <p className="text-sm text-grey-400 leading-relaxed">{d.body}</p>
            </div>
          ))}
        </div>
      </section>
      <FullDivider />

      <section className="py-16 md:py-20">
        <div className="px-5 sm:px-8 md:px-[66px] mb-12">
          <h2 className="text-[28px] md:text-[36px] font-bold text-white tracking-tight mb-3 leading-tight">{t("processTitle")}</h2>
          <p className="text-grey-400 text-[15px] leading-relaxed max-w-2xl">{t("processLead")}</p>
        </div>
        <div className="h-px bg-grey-800" />
        {o.process.map((s) => (
          <div
            key={s.title}
            className="grid grid-cols-[auto_1fr] md:grid-cols-[140px_minmax(0,1fr)_minmax(0,1.6fr)] gap-6 md:gap-10
                       px-5 sm:px-8 md:px-[66px] py-10 md:py-12
                       border-b border-grey-800 last:border-b-0
                       transition-colors hover:bg-[rgba(255,60,0,0.04)]"
          >
            <div className="text-xs font-mono uppercase tracking-widest self-start md:self-center" style={{ color: BRAND }}>{s.label}</div>
            <h3 className="text-lg md:text-xl font-bold text-white tracking-tight leading-tight self-start md:self-center">{s.title}</h3>
            <p className="text-grey-400 text-[14px] leading-relaxed col-span-2 md:col-span-1 self-center">{s.body}</p>
          </div>
        ))}
      </section>
      <FullDivider />

      <section className="px-5 sm:px-8 md:px-[66px] py-16 md:py-20">
        <h2 className="text-[28px] md:text-[36px] font-bold text-white tracking-tight mb-3 leading-tight">{t("pricingTitle")}</h2>
        <div className="mt-8 max-w-2xl">
          <div className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-5" style={{ color: BRAND }}>{o.pricing.headline}</div>
          <ul className="flex flex-col gap-3 mb-5">
            {o.pricing.lines.map((line) => (
              <li key={line} className="flex items-start gap-3 text-[14px] text-grey-300 leading-relaxed">
                <span className="font-bold mt-0.5 shrink-0 text-sm" style={{ color: BRAND }}>·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          {o.pricing.note && <p className="text-[13px] text-grey-500 italic">{o.pricing.note}</p>}
        </div>
      </section>
      <FullDivider />

      <section className="px-5 sm:px-8 md:px-[66px] py-16 md:py-20">
        <h2 className="text-[28px] md:text-[36px] font-bold text-white tracking-tight mb-10 leading-tight">{t("faqTitle")}</h2>
        <div className="flex flex-col">
          {o.faq.map((f) => (
            <div key={f.q} className="py-6 border-t border-grey-800 last:border-b last:border-b-grey-800 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-4 md:gap-10">
              <h3 className="text-base font-semibold text-white leading-snug">{f.q}</h3>
              <p className="text-[14px] text-grey-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
      <FullDivider />

      <section className="px-5 sm:px-8 md:px-[66px] py-20 md:py-24">
        <h2 className="text-[28px] md:text-[36px] font-bold text-white tracking-tight mb-4 leading-tight max-w-2xl">{t("ctaTitle")}</h2>
        <p className="text-grey-400 text-[15px] leading-relaxed max-w-2xl mb-8">{t("ctaLead", { offering: o.title })}</p>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
          >
            {t("bookCall")}
          </a>
          <a
            href="/pricing"
            onClick={(e) => { e.preventDefault(); navigate("/pricing"); }}
            className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
          >
            {t("fullPricing")}
          </a>
        </div>

        <div className="mt-14 pt-8 border-t border-grey-800">
          <div className="text-xs font-mono uppercase tracking-widest text-grey-500 mb-4">{t("relatedLabel")}</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {o.related.map((r) => (
              <a
                key={r.href}
                href={r.href}
                onClick={(e) => {
                  if (r.href.startsWith("#")) return;
                  e.preventDefault();
                  navigate(r.href);
                }}
                className="text-sm font-semibold text-grey-200 hover:text-white transition-colors"
              >
                {r.label} →
              </a>
            ))}
          </div>
        </div>
      </section>
    </SubPage>
  );
}
